import { attributionAnalyticsProjection } from "../../logic/helpers/attributionAnalytics";
import { GnomeAssetPreloadRuntime } from "./GnomeAssetPreloadRuntime/GnomeAssetPreloadRuntime";
import { useAnalyticsContext } from "../../logic/hooks/useAnalyticsContext";
import { syncCountryPersonProperties } from "../../logic/helpers/countryAnalyticsContext";
import { POSTHOG_ENABLED } from "../../state/config";
// src/components/app/Layout.tsx

import { useTonConnectUI } from "@tonconnect/ui-react";
import { useSlice } from "@bridge-applications/provider-kit";
import { lazy, Suspense, useEffect, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";
import { getTelegramWebApp } from "../../logic/helpers/telegram";
import { useTelegramAuth } from "../../logic/hooks/useTelegramAuth";
import { useSocialFriendInviteRedemption } from "../../logic/hooks/useSocialFriendInvites";
import SafeAreaView from "./SafeAreaView/SafeAreaView";
import { usePostHog } from "@posthog/react";
import { BackgroundMusicManager } from "./BackgroundMusicManager/BackgroundMusicManager";
import { IS_SCENE_LAB_ENABLED } from "../../logic/helpers/sceneLab";
import { identifyPostHogPerson } from "../../logic/helpers/analytics";
import { useCareerAccessRuntime } from "../../logic/hooks/useCareerAccess";
import { CommerceRewardCollection } from "./CommerceRewardCollection/CommerceRewardCollection";

const SceneLabLauncher = IS_SCENE_LAB_ENABLED
    ? lazy(() =>
          import("./SceneLabLauncher/SceneLabLauncher").then(
              ({ SceneLabLauncher: SceneLabLauncherComponent }) => ({
                  default: SceneLabLauncherComponent,
              }),
          ),
      )
    : null;

export default function Layout() {
    useTelegramAuth();
    useAnalyticsContext();
    useCareerAccessRuntime();
    const { state: authState } = useSlice("authSlice");
    const { state: analyticsContext } = useSlice("analyticsContextSlice");
    useSocialFriendInviteRedemption(
        authState.bootstrapStatus === "ready",
        authState.userId,
    );
    const posthog = usePostHog();
    const { i18n } = useTranslation();
    const [, setTonConnectOptions] = useTonConnectUI();

    useLayoutEffect(() => {
        attributionAnalyticsProjection.update(
            authState.bootstrapStatus === "ready" ? authState.userId : null,
            authState.attributionContext,
        );
        return () => attributionAnalyticsProjection.clear();
    }, [
        authState.bootstrapStatus,
        authState.userId,
        authState.attributionContext,
    ]);

    // ___associate this session's anonymous PostHog activity with the
    // canonical application user once authentication has completed
    useLayoutEffect(() => {
        if (authState.bootstrapStatus !== "ready" || !authState.userId) return;

        let storage: Storage | null = null;
        try {
            storage = window.localStorage;
        } catch {
            // Storage is only used to avoid unchanged person-property updates.
        }
        identifyPostHogPerson(posthog, storage, {
            userId: authState.userId,
            telegramUserId: authState.telegramUserId,
            accountCreatedAt: authState.accountCreatedAt,
            locale: i18n.resolvedLanguage ?? i18n.language,
        });
        if (
            POSTHOG_ENABLED &&
            analyticsContext.userId === authState.userId &&
            analyticsContext.countryContext?.state === "RESOLVED"
        ) {
            syncCountryPersonProperties(
                posthog,
                storage,
                authState.userId,
                analyticsContext.countryContext.estimate,
            );
        }
    }, [
        analyticsContext.userId,
        analyticsContext.countryContext,
        authState.bootstrapStatus,
        authState.accountCreatedAt,
        authState.telegramUserId,
        authState.userId,
        i18n.language,
        i18n.resolvedLanguage,
        posthog,
    ]);

    // TON Connect currently ships English and Russian UI strings. Keep its
    // modal aligned with the app selection and fall back to English elsewhere.
    useEffect(() => {
        setTonConnectOptions({
            language: i18n.resolvedLanguage === "ru" ? "ru" : "en",
        });
    }, [i18n.resolvedLanguage, setTonConnectOptions]);

    // ___keep the document color scheme in sync with the Telegram theme
    useEffect(() => {
        const webApp = getTelegramWebApp();
        if (!webApp) return;

        const applyColorScheme = () => {
            document.documentElement.dataset.colorScheme = webApp.colorScheme;
        };

        applyColorScheme();
        webApp.onEvent("themeChanged", applyColorScheme);
        return () => webApp.offEvent("themeChanged", applyColorScheme);
    }, []);

    // ___mirror Telegram's fullscreen state onto <body> so individual pages
    // can opt into their own manual offsets (e.g. `body.fullscreen ...`)
    // alongside the shared <SafeAreaView> wrapper below
    useEffect(() => {
        const webApp = getTelegramWebApp();
        if (!webApp) return;

        const applyFullscreenState = () => {
            document.body.classList.toggle("fullscreen", webApp.isFullscreen);
        };

        applyFullscreenState();
        webApp.onEvent("fullscreenChanged", applyFullscreenState);
        // ___requestFullscreen() can fail (unsupported platform, already
        // fullscreen, etc.); the expanded viewport from main.tsx still
        // applies, so there's nothing more to do here than keep the class in sync
        webApp.onEvent("fullscreenFailed", applyFullscreenState);
        return () => {
            webApp.offEvent("fullscreenChanged", applyFullscreenState);
            webApp.offEvent("fullscreenFailed", applyFullscreenState);
        };
    }, []);

    return (
        <SafeAreaView>
            <GnomeAssetPreloadRuntime />
            <BackgroundMusicManager />
            <Outlet />
            <CommerceRewardCollection />
            {SceneLabLauncher !== null && (
                <Suspense fallback={null}>
                    <SceneLabLauncher />
                </Suspense>
            )}
        </SafeAreaView>
    );
}
